import { readBarcodes, readBarcodesFromImageFile, matrixBarcodeFormats, linearBarcodeFormats, binarizers } from 'zxing-wasm';

/**
 * Read barcodes and QR codes from an image file using ZXing WASM
 * @param {File} file - The image file to read from
 * @returns {Promise<Array>} - Array of detected codes with their data and positions
 */
const DECODE_TIMEOUT_MS = 2000;

/** Downscale so longest side is at most this — full-res + 6× upscale was freezing the tab. */
const MAX_DECODE_LONG_SIDE = 2048;

/** Never feed WASM wider/taller than this (upscaled attempts included). */
const HARD_MAX_LONG_SIDE = 3000;

const formats = [...matrixBarcodeFormats, ...linearBarcodeFormats];
const binarizer = binarizers?.local_average || 'local_average';

function formatResult(result) {
  const text = result.text || result.content || result.data || result.value || '';
  const format = result.format || result.type || 'UNKNOWN';
  const position = result.position || result.location || { x: 0, y: 0 };
  return {
    text,
    format,
    position: { x: position.x || 0, y: position.y || 0 },
  };
}

function mapResults(results) {
  if (!results?.length) return [];
  return results.map(formatResult);
}

/**
 * Build a canvas scaled to fit within maxLongSide (maintains aspect ratio).
 */
function canvasFromImage(img, maxLongSide) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.min(1, maxLongSide / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  canvas.width = cw;
  canvas.height = ch;
  ctx.drawImage(img, 0, 0, cw, ch);
  return { canvas, ctx, width: cw, height: ch };
}

function capDimensions(width, height, maxLongSide) {
  const m = Math.max(width, height);
  if (m <= maxLongSide) return { width, height };
  const s = maxLongSide / m;
  return { width: Math.round(width * s), height: Math.round(height * s) };
}

export const readBarcodesFromImageWasm = async (file, opts = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log(`⏱️ WASM: Decode aborted after ${DECODE_TIMEOUT_MS}ms for`, file.name);
    controller.abort();
  }, DECODE_TIMEOUT_MS);

  try {
    return await _readBarcodesFromImageWasm(file, controller.signal, opts);
  } finally {
    clearTimeout(timeoutId);
  }
};

const _readBarcodesFromImageWasm = (file, signal, { requireSystemQR = false } = {}) => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const safeResolve = (val) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };

    const aborted = () => signal.aborted;

    const reader = new FileReader();

    reader.onload = (event) => {
      (async () => {
        try {
          if (aborted()) {
            safeResolve([]);
            return;
          }

          const imageDataUrl = event.target.result;
          const img = new Image();

          img.onload = async () => {
            try {
              if (aborted()) {
                safeResolve([]);
                return;
              }

              console.log('WASM: Image loaded:', img.width, 'x', img.height);

              // 0) Dedicated QR-only pass(es). A mixed decode tends to return the prominent
              // linear barcodes and skip the small system QR; matrix-only formats make ZXing
              // hunt QR finder patterns. The system QR (weight|trackingJp|orderId|boxNo|
              // shipDate, 5 fields) carries the boxNo, so capturing it ends the search.
              const isSystemQR = (c) => (c.text || '').split('|').length === 5;
              try {
                let qrResults = mapResults(await readBarcodesFromImageFile(file, {
                  formats: matrixBarcodeFormats,
                  tryHarder: true,
                }));
                // J4410: every label is expected to carry the system QR, so try harder —
                // a high-res grayscale pass often recovers a small/low-contrast QR.
                if (requireSystemQR && !qrResults.some(isSystemQR) && !aborted()) {
                  try {
                    const { ctx, width: gw, height: gh } = canvasFromImage(img, HARD_MAX_LONG_SIDE);
                    const gd = ctx.getImageData(0, 0, gw, gh);
                    const data = gd.data;
                    for (let i = 0; i < data.length; i += 4) {
                      const v = (data[i] + data[i + 1] + data[i + 2]) / 3;
                      data[i] = data[i + 1] = data[i + 2] = v;
                    }
                    const more = await readBarcodes({
                      data: gd.data, width: gd.width, height: gd.height,
                      bytesPerPixel: 4, formats: matrixBarcodeFormats, tryHarder: true, binarizer,
                    });
                    qrResults = [...qrResults, ...mapResults(more || [])];
                  } catch (gErr) {
                    console.log('WASM: J4410 grayscale QR retry failed:', gErr?.message || gErr);
                  }
                }
                if (qrResults.some(isSystemQR)) {
                  console.log('✅ WASM: system QR via dedicated pass:', qrResults);
                  safeResolve(qrResults);
                  return;
                }
              } catch (qrErr) {
                console.log('WASM: QR-only pass failed:', qrErr?.message || qrErr);
              }

              // 1) Try WASM file path first (often fast)
              try {
                console.log('WASM: Attempting to decode from file...');
                const directResults = await readBarcodesFromImageFile(file, {
                  formats,
                  tryHarder: true,
                });
                if (aborted()) {
                  safeResolve([]);
                  return;
                }
                console.log('WASM: Raw file results:', directResults);
                if (directResults?.length > 0) {
                  const formatted = mapResults(directResults);
                  console.log('✅ WASM: Found barcodes from file:', formatted);
                  safeResolve(formatted);
                  return;
                }
              } catch (fileErr) {
                console.log('WASM: File decode failed, falling back to canvas:', fileErr?.message || fileErr);
              }

              if (aborted()) {
                safeResolve([]);
                return;
              }

              // 2) Work on a downscaled bitmap — avoids multi‑second getImageData + WASM on 10k px images
              const { canvas, ctx, width: cw, height: ch } = canvasFromImage(img, MAX_DECODE_LONG_SIDE);
              console.log('WASM: Working canvas:', cw, 'x', ch, '(capped for decode)');

              const tryReadImageData = async (imageData) => {
                if (aborted()) return [];
                const results = await readBarcodes({
                  data: imageData.data,
                  width: imageData.width,
                  height: imageData.height,
                  bytesPerPixel: 4,
                  formats,
                  tryHarder: true,
                  binarizer,
                });
                return mapResults(results || []);
              };

              const tryGrayscaleRead = async (imageData) => {
                if (aborted()) return [];
                const gray = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
                const gd = gray.data;
                for (let i = 0; i < gd.length; i += 4) {
                  const v = (gd[i] + gd[i + 1] + gd[i + 2]) / 3;
                  gd[i] = v;
                  gd[i + 1] = v;
                  gd[i + 2] = v;
                }
                const results = await readBarcodes({
                  data: gray.data,
                  width: gray.width,
                  height: gray.height,
                  bytesPerPixel: 4,
                  formats,
                  tryHarder: true,
                  binarizer,
                });
                return mapResults(results || []);
              };

              // Original (already capped) canvas
              try {
                console.log('WASM: Attempting to decode from canvas...');
                const imageData = ctx.getImageData(0, 0, cw, ch);
                console.log('WASM: ImageData extracted:', imageData.width, 'x', imageData.height);
                let found = await tryReadImageData(imageData);
                if (found.length > 0) {
                  console.log('✅ WASM: Found barcodes:', found);
                  safeResolve(found);
                  return;
                }
                console.log('❌ WASM: No barcodes found with working size');

                if (aborted()) {
                  safeResolve([]);
                  return;
                }

                found = await tryGrayscaleRead(imageData);
                if (found.length > 0) {
                  console.log('✅ WASM: Found barcodes (grayscale):', found);
                  safeResolve(found);
                  return;
                }
              } catch (e) {
                console.log('WASM: Canvas decode error:', e?.message || e);
              }

              // 3) Limited upscales only (1.25×, 1.5×) with hard pixel cap — no more 6× full-res
              const factors = [1.25, 1.5];
              for (const f of factors) {
                if (aborted()) {
                  safeResolve([]);
                  return;
                }
                try {
                  let tw = Math.round(cw * f);
                  let th = Math.round(ch * f);
                  const capped = capDimensions(tw, th, HARD_MAX_LONG_SIDE);
                  tw = capped.width;
                  th = capped.height;

                  const testCanvas = document.createElement('canvas');
                  const testCtx = testCanvas.getContext('2d', { willReadFrequently: true });
                  testCtx.imageSmoothingEnabled = true;
                  testCanvas.width = tw;
                  testCanvas.height = th;
                  testCtx.drawImage(canvas, 0, 0, tw, th);

                  const testImageData = testCtx.getImageData(0, 0, tw, th);
                  console.log('WASM: Scaled try:', testImageData.width, 'x', testImageData.height);

                  let foundScaled = await tryReadImageData(testImageData);
                  if (foundScaled.length > 0) {
                    console.log('✅ WASM: Found barcodes (scaled):', foundScaled);
                    safeResolve(foundScaled);
                    return;
                  }
                  if (aborted()) {
                    safeResolve([]);
                    return;
                  }
                  foundScaled = await tryGrayscaleRead(testImageData);
                  if (foundScaled.length > 0) {
                    console.log('✅ WASM: Found barcodes (grayscale scaled):', foundScaled);
                    safeResolve(foundScaled);
                    return;
                  }
                } catch (sizeError) {
                  console.log('WASM: Scaled attempt failed:', sizeError?.message || sizeError);
                }
              }

              // 4) Rotations on working canvas only (small)
              if (aborted()) {
                safeResolve([]);
                return;
              }
              const rotations = [90, 180, 270];
              for (const angle of rotations) {
                if (aborted()) {
                  safeResolve([]);
                  return;
                }
                try {
                  const rot = document.createElement('canvas');
                  const rctx = rot.getContext('2d', { willReadFrequently: true });
                  if (angle % 180 === 0) {
                    rot.width = cw;
                    rot.height = ch;
                  } else {
                    rot.width = ch;
                    rot.height = cw;
                  }
                  rctx.translate(rot.width / 2, rot.height / 2);
                  rctx.rotate((angle * Math.PI) / 180);
                  rctx.drawImage(canvas, -cw / 2, -ch / 2);
                  const rd = rctx.getImageData(0, 0, rot.width, rot.height);
                  const rotResults = await tryReadImageData(rd);
                  if (rotResults.length > 0) {
                    console.log(`✅ WASM: Found barcodes with rotation ${angle}:`, rotResults);
                    safeResolve(rotResults);
                    return;
                  }
                } catch (rotErr) {
                  console.log('WASM: Rotation attempt failed:', rotErr?.message || rotErr);
                }
              }

              console.log('❌ WASM: No barcodes found in any approach');
              safeResolve([]);
            } catch (error) {
              console.error('❌ WASM: Error processing image:', error);
              if (!settled) reject(error);
            }
          };

          img.onerror = () => {
            console.error('❌ WASM: Failed to load image');
            if (!settled) reject(new Error('Failed to load image'));
          };

          img.src = imageDataUrl;
        } catch (error) {
          console.error('❌ WASM: Error in file reader:', error);
          if (!settled) reject(error);
        }
      })();
    };

    reader.onerror = () => {
      console.error('❌ WASM: Failed to read file');
      if (!settled) reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

export default { readBarcodesFromImageWasm };
